/****************
 * ENGAGEMENT: WHAT THE SCREENS SHOW FROM THE ENGAGEMENT ENDPOINTS. 2026-10-21.
 *
 * Twin file: frontend/src/lib/engagement.core.ts and
 * mobile-app/src/lib/engagement.core.ts must be byte-identical. Types of the
 * backend's answers (rest/trader_picks.go, rest/engagement.go) and the pure
 * rules the Dashboard, Traders and Get Started apply to them, so web and phone
 * decide the same thing from the same data.
 *
 * Nothing here chooses a copy setting (copyfi-no-defaults-from-us). Picks are
 * evidence with a stated reason; the next step after a loss is to learn why,
 * never to copy more (Guillaume, 2026-10-21).
 ****************/

export const ENGAGEMENT_PATHS = {
  picks: '/api/v1/dashboard/trader-picks',
  visit: '/api/v1/me/visit',
  recap: '/api/v1/me/recap',
  milestones: '/api/v1/me/milestones',
  celebrated: (key: string) => `/api/v1/me/milestones/${encodeURIComponent(key)}/celebrated`,
  pushPreferences: '/api/v1/push/preferences',
  review: '/api/v1/me/review',
  quiz: '/api/v1/me/quiz',
  achievements: '/api/v1/me/achievements',
} as const;

export type PickGroup = 'steady' | 'balanced' | 'bold';

export interface TraderPick {
  trader_uid: string;
  trader_name: string;
  trade_count: number;
  win_rate: number;
  total_realized_pnl: number;
  equity_drawdown_percent: number | null;
  calmar: number | null;
  profitable_months_percent: number | null;
  avg_duration_hours: number;
  avg_leverage?: number;
  return_percent: number | null;
  group: PickGroup;
}

export interface TraderPicks {
  steady: TraderPick[];
  balanced: TraderPick[];
  bold: TraderPick[];
  /** Every eligible trader, best first (round 8 #220); absent from an older server. */
  best?: TraderPick[];
  steady_max_fall: number;
  balanced_max_fall: number;
  eligible: number;
}

/** Get Started's three goals (Guillaume, 2026-10-21). */
export type Goal = 'steady' | 'higher' | 'learn';
export const GOALS: Goal[] = ['steady', 'higher', 'learn'];

/** The three traders a goal shows: Steady, Bold, or one of each to learn from. */
export function picksForGoal(p: TraderPicks | undefined, goal: Goal): TraderPick[] {
  if (!p) return [];
  if (goal === 'steady') return p.steady.slice(0, 3);
  if (goal === 'higher') return p.bold.slice(0, 3);
  return [p.steady[0], p.balanced[0], p.bold[0]].filter(Boolean) as TraderPick[];
}

/** "Picked for you": two per group, Steady first. */
export function pickedForYou(p: TraderPicks | undefined): { group: PickGroup; traders: TraderPick[] }[] {
  if (!p) return [];
  return (['steady', 'balanced', 'bold'] as PickGroup[])
    .map((group) => ({ group, traders: p[group].slice(0, 2) }))
    .filter((g) => g.traders.length > 0);
}

/** Trading style from how long a trader holds, for the style tag. */
export function styleOf(avgHours: number): 'scalper' | 'dayTrader' | 'swing' | 'position' {
  if (avgHours < 1) return 'scalper';
  if (avgHours < 24) return 'dayTrader';
  if (avgHours < 24 * 7) return 'swing';
  return 'position';
}

/* ── Recap ─────────────────────────────────────────────────────────────── */

export interface RecapTrade {
  id: string;
  subscription_id: number;
  trader_uid: string;
  symbol: string;
  direction: string;
  realized_pnl: number;
  roe: number;
  is_paper: boolean;
  exit_time: string;
}

export interface RecapSide {
  closed: number;
  wins: number;
  losses: number;
  net_pnl: number;
  best: RecapTrade | null;
  worst: RecapTrade | null;
  /** The copied trader's own record beside the hardest trade (rest/engagement.go). */
  worst_context?: {
    trade_count: number;
    win_rate: number;
    worst_trade_percent: number;
    equity_drawdown_percent: number | null;
  } | null;
}

export interface Recap {
  since: string;
  opened: number;
  open_now: number;
  paper: RecapSide;
  live: RecapSide;
  traders: string[];
  /** The closed trades of the period, newest first (rest/engagement.go, 10-25). */
  trades?: RecapTrade[];
}

export interface Visit {
  since: string | null;
  checkins_this_week: number;
}

/** The money the recap leads with: live when there is any, else paper. */
export function recapLead(r: Recap | undefined): { side: RecapSide; paper: boolean } | null {
  if (!r) return null;
  if (r.live.closed > 0) return { side: r.live, paper: false };
  if (r.paper.closed > 0) return { side: r.paper, paper: true };
  return null;
}

/** Nothing happened since the last visit (the recap then says so, in one line). */
export function recapQuiet(r: Recap | undefined): boolean {
  return !!r && r.opened === 0 && r.live.closed === 0 && r.paper.closed === 0;
}

/* ── Milestones ────────────────────────────────────────────────────────── */

export type MilestoneKey =
  | 'first_copy' | 'first_trade' | 'first_win' | 'ten_trades'
  | 'first_100' | 'week_in_profit' | 'three_traders' | 'first_live';

export interface Milestone {
  key: MilestoneKey;
  done: boolean;
  progress: number;
  remaining: number;
  unit: 'sessions' | 'trades' | 'wins' | 'usd' | 'days' | 'traders';
  achieved_at?: string;
  celebrated: boolean;
}

/** One quiz's record (rest/engagement_quiz.go): best score, tries, and when it
 *  was first done without a mistake (the badge). */
export interface QuizState {
  quiz: string;
  best: number;
  total: number;
  tries: number;
  perfect_at?: string;
}

export interface MilestoneState {
  milestones: Milestone[];
  next: Milestone | null;
}

/** Reached but not yet celebrated on any device: celebrate these, oldest first. */
export function toCelebrate(s: MilestoneState | undefined): Milestone[] {
  return (s?.milestones ?? []).filter((m) => m.done && !m.celebrated);
}

/** Bigger celebration for the milestones that mark a real step. */
export function celebrationSize(key: MilestoneKey): 'small' | 'big' {
  return key === 'first_trade' || key === 'first_100' || key === 'week_in_profit' || key === 'first_live'
    ? 'big' : 'small';
}

/* ── The next step ─────────────────────────────────────────────────────── */

export type NextStep =
  | { kind: 'getStarted' }
  | { kind: 'waitFirstTrade' }
  | { kind: 'learnLoss'; trade: RecapTrade }
  | { kind: 'tryLive'; closed: number; wins: number; netPnl: number };

/**
 * One next step, and only when there is something specific to say (Guillaume,
 * 2026-10-21: "they need to appear in specific context and present specific
 * insight"); otherwise null and nothing is shown. After a loss the step is to
 * understand it, never to copy more. tryLive carries the person's own practice
 * record, the insight it is based on.
 */
export function nextStep(m: MilestoneState | undefined, r: Recap | undefined): NextStep | null {
  if (!m) return null;
  const by = new Map(m.milestones.map((x) => [x.key, x]));
  if (!by.get('first_copy')?.done) return { kind: 'getStarted' };
  if (!by.get('first_trade')?.done) return { kind: 'waitFirstTrade' };
  const lead = recapLead(r);
  if (lead?.side.worst) return { kind: 'learnLoss', trade: lead.side.worst };
  if (by.get('ten_trades')?.done && !by.get('first_live')?.done && r && r.paper.closed > 0) {
    return { kind: 'tryLive', closed: r.paper.closed, wins: r.paper.wins, netPnl: r.paper.net_pnl };
  }
  return null;
}

/* ── The week (the shareable weekly card) ──────────────────────────────── */

/** Monday 00:00 of this week, in the person's own time zone, as ISO. */
export function weekStartISO(now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.toISOString();
}

/** The week's figures for the share card, real money first; null with nothing closed. */
export function weekCard(r: Recap | undefined): { side: RecapSide; paper: boolean; winRate: number } | null {
  const lead = recapLead(r);
  if (!lead) return null;
  return { ...lead, winRate: lead.side.closed ? (lead.side.wins / lead.side.closed) * 100 : 0 };
}

/* ── Featured traders (Guillaume 10-21: show them, never point toward one) ── */

/**
 * The traders CopyFi features, as one neutral list: no group labels, no
 * "picked for you", no reason one is better than another. Interleaved across
 * the engine's groups so the order itself does not rank them by risk.
 */
export function featuredTraders(p: TraderPicks | undefined, max = 6): TraderPick[] {
  if (!p) return [];
  // Only the best, best first (round 8 #220, Guillaume 10-27: Lite shows the "best" traders only).
  if (p.best?.length) return p.best.slice(0, max);
  const out: TraderPick[] = [];
  // Round robin over the three groups, as far as they go (round 7 #130: up to 120 on Lite's Traders).
  const depth = Math.max(p.steady.length, p.balanced.length, p.bold.length);
  for (let i = 0; out.length < max && i < depth; i++) {
    for (const g of [p.steady, p.balanced, p.bold]) {
      if (g[i] && out.length < max) out.push(g[i]);
    }
  }
  return out;
}

/* ── The review request (Guillaume 2026-10-21) ───────────────────────── */

export type ReviewMoment = 'first_session' | 'first_win';

/** Which milestone, once celebrated, may ask for a review. */
export function reviewMomentFor(key: MilestoneKey): ReviewMoment | null {
  // Only a win asks (Guillaume 10-25: never after a loss, always after a win).
  return key === 'first_win' ? 'first_win' : null;
}

/****************
 * ACHIEVEMENTS (rest/engagement_achievements.go), 2026-10-24. Bronze, silver,
 * gold per ladder, a showcase of 3 shown in Community, and one goal whose next
 * tier is "what's next". Icons are Phosphor names, mapped in each app.
 ****************/
export interface Achievement {
  key: string;
  tier: number; // 0 none, 1 bronze, 2 silver, 3 gold
  have?: number;
  tiers?: number[];
  unit?: string;
}

export interface AchievementState {
  achievements: Achievement[];
  showcase: string[];
  goal: string;
  /** Up to three goals tracked (Guillaume 10-25); goal is the first. */
  goals?: string[];
}

export const ACHIEVEMENT_ICON: Record<string, string> = {
  profitable_days: 'CalendarCheck',
  profitable_weeks: 'Star',
  growth: 'Rocket',
  trades: 'ArrowsDownUp',
  sessions: 'Play',
  brokers: 'Key',
  quizzes: 'Exam',
  community: 'ChatCircleDots',
  beat_trader: 'Trophy',
  wins: 'Medal',
  traders_copied: 'UsersThree',
  backtests: 'Flask',
  orb_questions: 'Sparkle',
};

/** Bronze, silver, gold; index = tier. */
export const TIER_COLOR = ['', '#CD7F32', '#C0C0C0', '#FFD700'];

/** The next threshold to reach, or null once gold. */
export function nextNeed(a: Achievement): number | null {
  const t = a.tiers ?? [];
  return a.tier < t.length ? t[a.tier] : null;
}

/** How a count reads: whole numbers, a percent for growth. */
export function achievementAmount(a: Achievement, n: number): string {
  return a.unit === 'pct' ? `${Math.floor(n)}%` : String(Math.floor(n));
}


/****************
 * THE PERFORMANCE CARD'S PERIODS. 2026-10-25, Guillaume: combine "Since you
 * were away" with "Your performance", with a time selector. A period other
 * than "all" reads the recap from its start and cuts the closed trades by the
 * card's own filters (scope, broker, asset type).
 ****************/
export const PERF_PERIODS = ['visit', 'day', 'week', 'month', 'all'] as const;
export type PerfPeriod = (typeof PERF_PERIODS)[number];

/** Where a period starts; null for "all" (the session totals answer it). */
export function periodSince(p: PerfPeriod, visitSince: string | null | undefined, now: number = Date.now()): string | null {
  const ago = (h: number) => new Date(Math.floor((now - h * 3_600_000) / 60_000) * 60_000).toISOString();
  if (p === 'all') return null;
  if (p === 'visit') return visitSince ?? ago(24);
  return ago(p === 'day' ? 24 : p === 'week' ? 24 * 7 : 24 * 30);
}

/**
 * The period the dashboard opens on (round 9 #304, Guillaume 10-27): the first of Last visit, Day,
 * Week, Month, All with a trade closed in it, so it never opens on "No trade closed" when one did.
 */
export function firstPeriodWithTrade(
  trades: RecapTrade[] | undefined,
  visitSince: string | null | undefined,
  keep: (t: RecapTrade) => boolean,
  now: number = Date.now(),
): PerfPeriod {
  const kept = (trades ?? []).filter(keep);
  for (const p of PERF_PERIODS) {
    if (p === 'visit' && !visitSince) continue;
    const since = periodSince(p, visitSince, now);
    if (since === null || kept.some((t) => t.exit_time >= since)) return p;
  }
  return 'all';
}

export interface PeriodSummary { closed: number; wins: number; losses: number; net: number; best: RecapTrade | null; worst: RecapTrade | null;
  /** Sum of the winning and the losing trades, for the profit factor (10-26). */
  grossWin: number; grossLoss: number }

/** The closed trades a filter keeps, summed; `worst` only when it lost money. */
export function summarisePeriod(trades: RecapTrade[] | undefined, keep: (t: RecapTrade) => boolean): PeriodSummary {
  const s: PeriodSummary = { closed: 0, wins: 0, losses: 0, net: 0, best: null, worst: null, grossWin: 0, grossLoss: 0 };
  for (const t of trades ?? []) {
    if (!keep(t)) continue;
    s.closed++;
    s.net += t.realized_pnl;
    if (t.realized_pnl > 0) { s.wins++; s.grossWin += t.realized_pnl; }
    else if (t.realized_pnl < 0) { s.losses++; s.grossLoss -= t.realized_pnl; }
    if (!s.best || t.realized_pnl > s.best.realized_pnl) s.best = t;
    if (!s.worst || t.realized_pnl < s.worst.realized_pnl) s.worst = t;
  }
  if (s.best && s.best.realized_pnl <= 0) s.best = null;
  if (s.worst && s.worst.realized_pnl >= 0) s.worst = null;
  return s;
}

/** The figures that sum a period up (Guillaume 10-26: "more data that sums up performance"). */
export function periodFigures(s: PeriodSummary): { winRate: number | null; avgTrade: number | null; profitFactor: number | null } {
  return {
    winRate: s.closed > 0 ? (100 * s.wins) / s.closed : null,
    avgTrade: s.closed > 0 ? s.net / s.closed : null,
    profitFactor: s.grossLoss > 0 ? s.grossWin / s.grossLoss : null,
  };
}
